import { environment } from '../../../environments/environment';
import { ResourceType } from '../types/resource-item.class';

/**
 * Which kinds of content the app offers.
 *
 * Shorts, video and audio are built and stay built - the pages, the routes, the
 * cards and the bookmarks all still work. They are simply not offered in this
 * release (Toma, 28 Oct 2026): the library behind them is not ready to be shown
 * to members, and switching a list off is a decision we want to take back in one
 * line rather than in a revert.
 *
 * So this is the one place that answers "is this type on?". Everything that
 * lists, searches, links to or routes into a type asks here. To bring a type
 * back, add it to `enabledContentTypes` in the environment files and build; no
 * other file needs to change.
 */

/** Every type the API can return, whether or not we currently offer it. */
export const ALL_CONTENT_TYPES: readonly ResourceType[] = ['article', 'guide', 'short', 'audio', 'video'];

/**
 * The types this build offers, in the environment's order. Anything the
 * environment names that is not a real type is ignored rather than trusted, so a
 * typo cannot invent a tab; an environment with no list at all means "all of
 * them", which is what an older environment file implies.
 */
export const ENABLED_CONTENT_TYPES: readonly ResourceType[] = (() => {
    const configured = (environment as { enabledContentTypes?: string[] }).enabledContentTypes;

    if (!Array.isArray(configured)) {
        return ALL_CONTENT_TYPES;
    }

    return ALL_CONTENT_TYPES.filter(type => configured.includes(type));
})();

/** Is this type offered? Unknown or missing types are not. */
export function isContentTypeEnabled(type: string | null | undefined): boolean {
    return !!type && (ENABLED_CONTENT_TYPES as readonly string[]).includes(type);
}

/**
 * The tab pills a listing should show. 'all' and anything that is not a content
 * type (a saved/bookmark pill, say) are left alone; type tabs survive only while
 * their type is offered.
 */
export function enabledTypeTabs<T extends string>(tabs: readonly T[]): T[] {
    return tabs.filter(tab => !(ALL_CONTENT_TYPES as readonly string[]).includes(tab) || isContentTypeEnabled(tab));
}

/**
 * Drop the items a listing should not show. The API still returns every type -
 * filtering it there is a separate decision - so the last word is here, and a
 * card for a type we do not offer never reaches the page.
 */
export function keepEnabledTypes<T extends { type?: string | null }>(items: T[]): T[] {
    return (items ?? []).filter(item => isContentTypeEnabled(item?.type));
}
